// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// PMS integration (D-PMS-1). Names which dispensing system this pharmacy runs
// and what OwlRx can do against it. Vendor-scoped by design: the capability list
// and the connectivity result come from the vendor's own API, not from a fixed
// PioneerRx assumption baked into the page.

import { useEffect, useState } from 'react'
import { Activity, CheckCircle2, AlertTriangle, RefreshCw, Link2 } from 'lucide-react'
import * as api from '../../api/client'

const card: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 8, padding: 16,
  background: 'var(--color-surface, #fff)', marginBottom: 16,
}
const lbl: React.CSSProperties = {
  fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
  color: 'var(--color-text-muted)', fontWeight: 600,
}
const th: React.CSSProperties = {
  textAlign: 'left', padding: '7px 10px', fontSize: 11, textTransform: 'uppercase',
  letterSpacing: 0.4, color: 'var(--color-text-muted)', fontWeight: 600,
  borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap',
}
const td: React.CSSProperties = {
  padding: '7px 10px', fontSize: 13, borderBottom: '1px solid var(--color-border)',
  verticalAlign: 'top',
}
const btn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px',
  fontSize: 13, fontWeight: 600, cursor: 'pointer', borderRadius: 6,
  border: '1px solid var(--color-border)', background: 'var(--color-surface, #fff)',
  color: 'var(--color-text)',
}
const mono: React.CSSProperties = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5 }


// ── Current PMS integrations ─────────────────────────────────────────────
// ONLY systems with EVIDENCE of a callable API are listed. An earlier version of
// this list carried 16 vendors, every one of them labelled as integrable — that
// was a market survey wearing the clothes of a capability claim, and a prospect
// would have found it out on day one.
//
// Each entry below was checked on 2026-08-27, by reading the vendor's published
// spec or by calling their API host directly:
//
//   Liberty    devapi.libertysoftware.com serves a public OpenAPI 3.0 document
//              (56 paths). Called live: 401 "Basic authentication required".
//   PioneerRx  no public API host resolves, but OwlRx integrates it in production
//              TODAY via the Rx Event webhook + ingest. Working beats documented.
//   PrimeRx    api.primerx.io is a live JSON API gateway (returns structured
//              {statusCode, message} errors, not a web page). Spec not public.
//   Epic       fhir.epic.com / open.epic.com / apporchard.epic.com are live public
//              developer programmes. Access needs app registration plus the health
//              system's approval.
//
// DELIBERATELY REMOVED for want of evidence — no API host resolved and no public
// developer documentation found: DigitalRx, Datascan, KeyCentrix, SuiteRx, SRS,
// Speed Script, ScriptPro, QS/1, BestRx, Rx30, Computer-Rx, McKesson EnterpriseRx.
// Absence of a PUBLIC API is not proof there is no integration path — most of
// these run partner/interface programmes — but it is not something to claim on a
// screen a customer reads. Re-add any of them the moment a spec is in hand.
type PmsStatus = 'verified' | 'production' | 'credentialed'
interface PmsApiLine { method: string; path: string; use: string }
interface PmsEntry {
  name: string; group: string; status: PmsStatus; basis: string; vendorKey?: string
  /** Where the contract came from, shown at the top of the detail panel. */
  specNote?: string
  /** Endpoints/resources. Omitted for Liberty: its list arrives live from the
   *  backend, transcribed from the vendor's own OpenAPI document. */
  api?: PmsApiLine[]
  /** Direction of travel. PioneerRx PUSHES to us; the others we CALL. */
  direction?: string
}

const PMS_DIRECTORY: PmsEntry[] = [
  { name: 'Liberty Software', group: 'Public API', status: 'verified', vendorKey: 'liberty',
    basis: 'Public OpenAPI 3.0 spec, 56 endpoints. Read and called live.' },
  { name: 'Epic (Willow)', group: 'Public API', status: 'credentialed',
    basis: 'Epic on FHIR. App registration plus the health system’s approval.',
    specNote: `Epic on FHIR (R4) — public spec at fhir.epic.com. Standard FHIR resources rather than a vendor-specific API, so the mapping is the same at every Epic site. Access needs an app registration and the health system’s own approval.`,
    direction: 'OwlRx calls Epic',
    api: [
      { method: 'GET', path: 'Patient', use: 'Demographics and address' },
      { method: 'GET', path: 'MedicationRequest', use: 'The prescription' },
      { method: 'GET', path: 'MedicationDispense', use: 'Fill-grain dispense records' },
      { method: 'GET', path: 'Coverage', use: 'Payer and copay context' },
      { method: 'GET', path: 'Practitioner', use: 'Prescriber directory' },
      { method: 'GET', path: 'DocumentReference', use: 'Clinical documents' },
    ] },
  { name: 'PioneerRx', group: 'Integrated by OwlRx', status: 'production', vendorKey: 'pioneerrx',
    basis: 'Live for OwlRx tenants today via the Rx Event webhook and ingest.',
    specNote: `PUSH rather than pull. PioneerRx posts Rx events to an endpoint OwlRx exposes per pharmacy; there is no public PioneerRx API for us to call. These are OwlRx’s own endpoints, in production today.`,
    direction: 'PioneerRx posts to OwlRx',
    api: [
      { method: 'POST', path: '/pioneerrx/ingest/{slug}', use: 'Rx event webhook, one URL per pharmacy' },
      { method: 'GET',  path: '/pioneerrx/events', use: 'Event history and replay' },
      { method: 'GET',  path: '/pioneerrx/staging', use: 'Parsed envelopes awaiting projection' },
      { method: 'POST', path: '/pioneerrx/mirror-now', use: 'Force a projection pass' },
      { method: 'GET',  path: '/carerx/integrations/pioneerrx/status', use: 'Sync health' },
    ] },
  { name: 'PrimeRx (Micro Merchant)', group: 'Credentialed API', status: 'credentialed',
    basis: 'Live API gateway at api.primerx.io. Credentials issued by the vendor.',
    specNote: `api.primerx.io answers as a JSON API (structured {statusCode, message} errors rather than a web page), so the service is live. The endpoint contract is NOT published — it comes with the vendor agreement, and we will map it the way Liberty is mapped once we hold it.`,
    direction: 'OwlRx calls PrimeRx',
    api: [] },
]

const STATUS_COPY: Record<PmsStatus, { label: string; tone: string; weight: number }> = {
  verified:     { label: 'Connected',          tone: 'var(--color-success, #1d6b3a)', weight: 700 },
  production:   { label: 'Live in production', tone: 'var(--color-success, #1d6b3a)', weight: 600 },
  credentialed: { label: 'API available',      tone: 'var(--color-text)',             weight: 500 },
}

function PmsDirectory({ connectedVendor, libertyApi }: {
  connectedVendor?: string
  /** Liberty's endpoint list, live from the backend and transcribed from the
   *  vendor's own OpenAPI document — never hardcoded here. */
  libertyApi?: api.PmsCapability[]
}) {
  const groups = Array.from(new Set(PMS_DIRECTORY.map(p => p.group)))
  const [open, setOpen] = useState<string | null>(null)
  const sel = PMS_DIRECTORY.find(p => p.name === open) ?? null
  // Liberty's rows come from the API; every other vendor carries its own.
  const rows: PmsApiLine[] = sel
    ? (sel.vendorKey === 'liberty'
        ? (libertyApi ?? []).map(c => ({ method: c.method, path: c.path, use: c.owlrx_use }))
        : (sel.api ?? []))
    : []
  return (
    <div style={card}>
      <div style={lbl}>Current PMS integrations</div>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 6, marginBottom: 12 }}>
        The dispensing systems OwlRx integrates with today. The platform is PMS-agnostic by
        design — any other system that exposes an API can be added, and its connector is built
        during onboarding.
      </p>
      <div style={{ marginBottom: 10 }} />
      {groups.map(g => (
        <div key={g} style={{ marginBottom: 12 }}>
          <div style={{ ...lbl, marginBottom: 6 }}>{g}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {PMS_DIRECTORY.filter(p => p.group === g).map(p => {
              const isConnected = !!connectedVendor && p.vendorKey === connectedVendor
              const st: PmsStatus = isConnected ? 'verified' : p.status
              const c = STATUS_COPY[st]
              return (
                <button
                  key={p.name}
                  type="button"
                  onClick={() => setOpen(open === p.name ? null : p.name)}
                  aria-expanded={open === p.name}
                  title={`Show the ${p.name} API`}
                  style={{
                    textAlign: 'left', font: 'inherit', cursor: 'pointer',
                    border: open === p.name
                      ? '2px solid var(--color-primary, #2563eb)'
                      : isConnected ? '2px solid var(--color-success, #1d6b3a)' : '1px solid var(--color-border)',
                    borderRadius: 7, padding: '9px 12px', minWidth: 232, maxWidth: 300,
                    background: 'var(--color-bg, #f8fafc)', color: 'var(--color-text)',
                  }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{p.name}</div>
                  <div style={{ fontSize: 11.5, marginTop: 2, color: c.tone, fontWeight: c.weight }}>
                    {c.label}
                  </div>
                  {p.basis && (
                    <div style={{ fontSize: 11, marginTop: 3, color: 'var(--color-text-muted)', lineHeight: 1.35 }}>
                      {p.basis}
                    </div>
                  )}
                  <div style={{ fontSize: 11, marginTop: 5, color: 'var(--color-primary, #2563eb)', fontWeight: 600 }}>
                    {open === p.name ? 'Hide API' : 'View API'}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      ))}
      {sel && (
        <div style={{
          border: '1px solid var(--color-border)', borderLeft: '3px solid var(--color-primary, #2563eb)',
          borderRadius: 8, padding: 14, margin: '4px 0 14px', background: 'var(--color-surface, #fff)',
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 15.5, fontWeight: 700 }}>{sel.name}</div>
            {sel.direction && (
              <span style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>{sel.direction}</span>
            )}
          </div>
          {sel.specNote && (
            <p style={{ fontSize: 12.5, color: 'var(--color-text-muted)', marginTop: 6, marginBottom: 10, lineHeight: 1.5 }}>
              {sel.specNote}
            </p>
          )}
          {rows.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Endpoint</th>
                    <th style={th}>Used for</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={`${r.method}${r.path}`}>
                      <td style={{ ...td, ...mono, whiteSpace: 'nowrap' }}>
                        <span style={{ fontWeight: 700 }}>{r.method}</span> {r.path}
                      </td>
                      <td style={td}>{r.use}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            // An empty list is a real answer, not a gap to paper over.
            <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>
              No endpoint contract published. It arrives with the vendor agreement, and is mapped
              the same way Liberty is once we hold it.
            </div>
          )}
        </div>
      )}

      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>
        Each integration above was verified against the vendor’s own API on 27 Aug 2026. Adding a new
        dispensing system is a normal onboarding step, not a platform change.
      </div>
    </div>
  )
}

export default function PmsIntegrationTab() {
  const [status, setStatus] = useState<api.LibertyPmsStatus | null>(null)
  const [test, setTest] = useState<api.LibertyPmsTest | null>(null)
  const [loading, setLoading] = useState(true)
  const [testing, setTesting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  function readErr(e: unknown) {
    const b = (e as { body?: { message?: string; error?: string } }).body
    return b?.message || b?.error || (e as Error).message || 'Request failed.'
  }

  useEffect(() => {
    void (async () => {
      try { setStatus(await api.getLibertyPmsStatus()) }
      catch (e) { setErr(readErr(e)) }
      finally { setLoading(false) }
    })()
  }, [])

  async function runTest() {
    setTesting(true); setErr(null)
    try { setTest(await api.testLibertyPms()) }
    catch (e) { setErr(readErr(e)) }
    finally { setTesting(false) }
  }

  if (loading) return <div style={{ padding: 16, fontSize: 13 }}>Loading…</div>

  const configured = !!status?.configured

  return (
    <div style={{ maxWidth: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Link2 size={18} />
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Pharmacy management system</h2>
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 0 }}>
        OwlRx does not replace your dispensing system — it reads from it and writes delivery
        evidence back. Any PMS with an API can be connected.
      </p>

      {err && (
        <div style={{ ...card, borderColor: 'var(--color-danger, #b91c1c)' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 13 }}>{err}</div>
          </div>
        </div>
      )}

      <PmsDirectory connectedVendor={status?.vendor} libertyApi={status?.capabilities} />

      <div style={card}>
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          <div>
            <div style={lbl}>Vendor</div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>{status?.display_name ?? '—'}</div>
          </div>
          <div>
            <div style={lbl}>Connected</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{configured ? 'Yes' : 'Not configured'}</div>
          </div>
          <div>
            <div style={lbl}>Mode</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{status?.mode}</div>
          </div>
          <div style={{ minWidth: 240 }}>
            <div style={lbl}>Endpoint</div>
            <div style={mono}>{status?.base_url}</div>
          </div>
        </div>

        <div style={{ marginTop: 14, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" style={btn} onClick={runTest} disabled={testing}>
            <RefreshCw size={14} /> {testing ? 'Contacting Liberty…' : 'Test connection'}
          </button>
          {status?.spec && (
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
              Mapped from their OpenAPI {status.spec.openapi} spec · {status.spec.paths_published} published endpoints
            </span>
          )}
        </div>

        {test && (
          <div style={{
            marginTop: 12, padding: 11, borderRadius: 6, fontSize: 13,
            border: '1px solid var(--color-border)', background: 'var(--color-bg, #f8fafc)',
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              {test.reachable
                ? <CheckCircle2 size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                : <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />}
              <div>
                <div style={{ fontWeight: 600 }}>{test.summary}</div>
                <div style={{ ...mono, marginTop: 4, color: 'var(--color-text-muted)' }}>
                  HTTP {test.http_status ?? '—'} · {test.elapsed_ms} ms · {test.detail}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* The two facts that decide whether an integration is deep or cosmetic. */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Fact on={!!status?.fill_grain_native}
              title="Fill identity is native"
              body="Liberty addresses a prescription as scriptNumber + refillNumber, the same base-vs-refill grain OwlRx enforces. 900001 and 900001-1 stay distinct across every hop." />
        <Fact on={!!status?.writeback_supported}
              title="Delivery evidence writes back"
              body="POST /shipping/pod and POST /shipping/shipment mean proof of delivery returns into the PMS, rather than living only in OwlRx." />
      </div>

      <div style={{ ...card, marginBottom: 0 }}>
        <div style={lbl}>Credentials</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>
          Liberty authenticates with <b>HTTP Basic</b> plus a <code style={mono}>Customer</code> header
          carrying <code style={mono}>base64(NPI:Key)</code>. Both are issued by Liberty to the pharmacy.
          OwlRx holds none for this tenant, which is why the connection test above reports
          “authentication required” rather than a green tick.
        </div>
        <div style={{ fontSize: 12.5, marginTop: 8, color: 'var(--color-text-muted)' }}>
          Sandbox and live reads stay gated behind a signed BAA, the pharmacy's NPI and API key,
          a risk analysis and Security Officer attestation. Until then this tenant reads synthetic
          Liberty-shaped records so the field mapping can be reviewed without any PHI.
        </div>
      </div>
    </div>
  )
}

function Fact({ on, title, body }: { on: boolean; title: string; body: string }) {
  return (
    <div style={{
      flex: '1 1 300px', border: '1px solid var(--color-border)', borderRadius: 8,
      padding: 14, background: 'var(--color-surface, #fff)',
    }}>
      <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 5 }}>
        {on ? <CheckCircle2 size={15} /> : <Activity size={15} />}
        <div style={{ fontWeight: 700, fontSize: 14 }}>{title}</div>
      </div>
      <div style={{ fontSize: 12.8, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>{body}</div>
    </div>
  )
}
